import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { keepWithinSource } from "./keepWithinSource.js";
import type { SeriesItem, VodItem } from "@hybrid-iptv/types";
import { api, runsOnDevice } from "../api/client.js";
import { nativeCatalogAvailable } from "../api/nativeCatalog.js";
import type { DiscoveryEntry, DiscoveryRelatedResult } from "../api/nativeCatalog.js";
import { providerBody } from "../epg/api.js";
import { displayTitle } from "../home/shelfFill.js";
import { useDiscoveryIndex } from "../discovery/discoveryFiles.js";
import { webDiscoveryRelated } from "../discovery/webDiscovery.js";
import { useVodCatalog } from "./vodHooks.js";
import { useProfiles } from "../settings/profileStore.js";
import { useProgressStore, watchProgressForProfile } from "./progressStore.js";
import { useFavoriteRefs } from "./useFavorites.js";
import { useLockedCategoryIds } from "./useCatalogQuery.js";
import { usesTmdb } from "../discovery/rowSupplier.js";
import type { RelatedTitle } from "./TitleDetail.js";

/**
 * A title page's Similar and You May Also Like under Open (Section 26 step 4): worked out
 * on the device from the discovery files over the catalog (CatalogStore.discoveryRelated),
 * so every poster is playable. The profile's history and My List go to the device as
 * catalog ids and shape You May Also Like there; nothing leaves the device.
 */

/**
 * Whether the Movies and Series pages can order by popularity: Open rows, on the device
 * (CatalogStore's "popular" sort over discovery.db) and on the website (useCatalogQuery orders
 * the catalog by the discovery files' views).
 */
export function popularSortAvailable(): boolean {
  return !usesTmdb() && (!runsOnDevice() || nativeCatalogAvailable());
}

/** Posters per row: TitleDetailExtras shows ten. */
const SIZE = 10;

export interface OpenRelated<T> {
  similar: RelatedTitle[];
  youMayLike: RelatedTitle[];
  /** TMDB id → the catalog title a poster opens. */
  byTmdb: Map<number, T>;
}

const NONE = { similar: [], youMayLike: [], byTmdb: new Map() };

/** A related entry as a poster: the discovery name and year, the provider's poster and rating. */
export function relatedPoster(entry: DiscoveryEntry): RelatedTitle | null {
  const item = entry.item as { rawTitle: string; posterUrl?: string; rating?: number; year?: number };
  if (!item.posterUrl) return null;
  const year = entry.year ?? item.year;
  return {
    id: entry.tmdb,
    title: entry.label ?? displayTitle(item.rawTitle),
    posterUrl: item.posterUrl,
    ...(year ? { release_date: String(year) } : {}),
    ...(item.rating !== undefined ? { vote_average: item.rating } : {}),
  };
}

export function openRelated<T>(result: DiscoveryRelatedResult): OpenRelated<T> {
  const byTmdb = new Map<number, T>();
  const posters = (entries: DiscoveryEntry[]) =>
    entries.flatMap((entry) => {
      const poster = relatedPoster(entry);
      if (!poster) return [];
      if (!byTmdb.has(entry.tmdb)) byTmdb.set(entry.tmdb, entry.item as T);
      return [poster];
    });
  return { similar: posters(result.similar), youMayLike: posters(result.youMayLike), byTmdb };
}

export function useOpenRelated<T extends VodItem | SeriesItem>(
  sourceId: string,
  kind: "vod" | "series",
  item: T | null,
  enabled: boolean,
): OpenRelated<T> {
  const web = !runsOnDevice();
  const device = runsOnDevice() && nativeCatalogAvailable();
  const on = enabled && (device || web) && item !== null;
  const profileId = useProfiles((s) => s.activeProfileId);
  const kids = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId)?.isKids === true);
  const entries = useProgressStore((s) => s.entries);
  // Films: what the profile watched; series: episodes do not carry their series, so My List alone.
  const watchedIds = useMemo(
    () => (kind === "vod" ? watchProgressForProfile(entries, profileId).filter((e) => e.contentType === "vod").map((e) => e.contentRef) : []),
    [kind, entries, profileId],
  );
  const favouriteIds = useFavoriteRefs(kind === "vod" ? "movie" : "series");
  const locked = useLockedCategoryIds(sourceId, kind, on && device);
  const catalog = useVodCatalog(sourceId, on && web && Boolean(sourceId));
  const index = useDiscoveryIndex(on && web);
  const items = kind === "series" ? catalog.data?.series : catalog.data?.vod;
  const webRelated = useMemo(
    () =>
      on && web && index && items && item
        ? webDiscoveryRelated(index, items, { kind, itemId: item.id, size: SIZE, kids, watchedIds, favouriteIds })
        : null,
    [on, web, index, items, item, kind, kids, watchedIds, favouriteIds],
  );
  const query = useQuery({
    queryKey: ["discoveryRelated", sourceId, kind, item?.id, kids, watchedIds, favouriteIds, locked],
    queryFn: () =>
      api.post<DiscoveryRelatedResult | null>(`/api/sources/${encodeURIComponent(sourceId)}/discovery/related`, {
        ...providerBody(),
        request: { kind, itemId: item!.id, size: SIZE, kids, watchedIds, favouriteIds },
        excludeCategories: locked,
      }),
    enabled: on && device && Boolean(sourceId) && locked !== undefined,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepWithinSource(sourceId),
    retry: false,
  });
  return useMemo(() => {
    const answer = web ? webRelated : query.data;
    return on && answer ? openRelated<T>(answer) : (NONE as OpenRelated<T>);
  }, [on, web, webRelated, query.data]);
}
